import { useEffect, useRef } from 'react';
import { prefersReducedMotion, watchReducedMotion } from './reducedMotion';

/**
 * Writes the element's travel through the viewport as `--sp` (0 = entering from below,
 * 1 = fully past the top). Measurement is gated behind an IntersectionObserver so
 * off-screen sections never touch the scroll handler.
 */
export function useScrollProgress<T extends HTMLElement>(variable = '--sp') {
  const ref = useRef<T>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    let detach: (() => void) | null = null;

    const bind = () => {
      let frame = 0;
      let visible = false;

      const measure = () => {
        frame = 0;
        const rect = node.getBoundingClientRect();
        const span = window.innerHeight + rect.height;
        const travelled = window.innerHeight - rect.top;
        node.style.setProperty(variable, Math.min(1, Math.max(0, travelled / span)).toFixed(4));
      };

      const request = () => {
        if (!frame && visible) frame = requestAnimationFrame(measure);
      };

      const observer = new IntersectionObserver(
        ([entry]) => {
          visible = entry.isIntersecting;
          if (visible) request();
        },
        { threshold: 0 },
      );
      observer.observe(node);
      window.addEventListener('scroll', request, { passive: true });
      window.addEventListener('resize', request, { passive: true });
      measure();

      return () => {
        observer.disconnect();
        window.removeEventListener('scroll', request);
        window.removeEventListener('resize', request);
        if (frame) cancelAnimationFrame(frame);
      };
    };

    const sync = (reduced: boolean) => {
      detach?.();
      detach = null;
      if (reduced) {
        node.style.setProperty(variable, '0.5');
        return;
      }
      detach = bind();
    };

    sync(prefersReducedMotion());
    const unwatch = watchReducedMotion(sync);

    return () => {
      unwatch();
      detach?.();
    };
  }, [variable]);

  return ref;
}
